import { registerTool } from '../ToolRegistry'
import { getSharedToolRegistration } from '../sharedToolCatalog'
import { useCharacterStore } from '@/stores/characterStore'

async function resolveCharacter(args = {}) {
  const cs = useCharacterStore()
  let list = []
  try {
    list = (await cs.fetchCharacters?.()) || cs.characters || []
  } catch {
    list = cs.characters || []
  }

  const current =
    (typeof cs?.getCurrentCharacter === 'function'
      ? cs.getCurrentCharacter()
      : cs?.currentCharacter) || null

  const normalizeName = (v) =>
    String(v || '')
      .trim()
      .toLowerCase()
  const targetId = args?.characterId ? String(args.characterId) : null
  const targetName = args?.name ? String(args.name) : null
  const candidates = Array.isArray(list) ? list : []

  let match = null
  if (targetId) {
    match = candidates.find((c) => String(c?.id) === targetId)
  }
  if (!match && targetName) {
    const needle = normalizeName(targetName)
    match = candidates.find((c) => {
      const nm = normalizeName(c?.name || c?.title)
      return nm && (nm === needle || nm.includes(needle))
    })
  }
  if (!match && current) match = current
  if (!match && candidates.length === 1) match = candidates[0]

  return { character: match || null, list: candidates, source: match ? 'resolved' : null }
}

export function registerCharacterTools() {
  registerTool({
    ...getSharedToolRegistration('list_characters'),
    handler: async () => {
      try {
        const cs = useCharacterStore()
        const list = (await cs.fetchCharacters?.()) || cs.characters || []
        const deduped = Array.isArray(list)
          ? list
              .filter((c) => c && c.id)
              .map((c) => ({
                id: c.id,
                name: c.name || c.title || 'Unnamed',
                level:
                  c.level ||
                  (Array.isArray(c.classes)
                    ? c.classes.reduce((n, cl) => n + (Number(cl.level) || 0), 0)
                    : null),
                classes: Array.isArray(c.classes)
                  ? c.classes.map((cl) => `${cl.name || ''} ${cl.level || 0}`.trim()).join(', ')
                  : ''
              }))
          : []
        return { ok: true, count: deduped.length, characters: deduped }
      } catch (e) {
        return { ok: false, error: e?.message || 'Failed to list characters' }
      }
    }
  })

  registerTool({
    ...getSharedToolRegistration('get_character_sections'),
    handler: async (args) => {
      try {
        if (!args?.characterId && !args?.name) {
          return {
            ok: false,
            error:
              'Please provide a "name" or "characterId" argument. Use list_characters to find available characters.'
          }
        }
        const { character } = await resolveCharacter(args)
        if (!character) return { ok: false, error: 'Character not found' }

        const requested =
          Array.isArray(args?.sections) && args.sections.length
            ? args.sections
            : ['background', 'personality', 'equipped', 'actions', 'defenses', 'traits', 'spells']

        const onlyNonEmpty = (val) => {
          if (Array.isArray(val)) return val.filter(Boolean).length ? val.filter(Boolean) : null
          if (val && typeof val === 'object') {
            const entries = Object.entries(val).filter(([, v]) => v != null && v !== '')
            return entries.length ? Object.fromEntries(entries) : null
          }
          return val ? val : null
        }

        const equipSection = () => {
          const eq = character.equipment || {}
          const items = Array.isArray(eq.items) ? eq.items : []
          const weapons = Array.isArray(eq.weapons) ? eq.weapons : []
          const equippedItems = items.filter((it) => it && it.equipped)
          const equippedWeapons = weapons.filter((w) => w && w.equipped)
          const armor = eq.armor || null
          const shield = eq.shield ?? null
          return onlyNonEmpty({ armor, shield, items: equippedItems, weapons: equippedWeapons })
        }

        const inventorySection = () => {
          const eq = character.equipment || {}
          const items = Array.isArray(eq.items) ? eq.items : []
          const weapons = Array.isArray(eq.weapons) ? eq.weapons : []
          const currency = eq.currency || character.currency || null
          const containers = Array.isArray(eq.containers) ? eq.containers : []
          const unequippedItems = items.filter((it) => it && !it.equipped)
          const unequippedWeapons = weapons.filter((w) => w && !w.equipped)
          return onlyNonEmpty({
            items: unequippedItems,
            weapons: unequippedWeapons,
            currency,
            containers
          })
        }

        const actionsSection = () => {
          const actions = Array.isArray(character.actions) ? character.actions : []
          const bonus = Array.isArray(character.bonusActions) ? character.bonusActions : []
          const reactions = Array.isArray(character.reactions) ? character.reactions : []
          return onlyNonEmpty({ actions, bonusActions: bonus, reactions })
        }

        const defensesSection = () => {
          const immunities = Array.isArray(character.immunities) ? character.immunities : []
          const resistances = Array.isArray(character.resistances) ? character.resistances : []
          const conditions = Array.isArray(character.conditions) ? character.conditions : []
          const senses = Array.isArray(character.senses) ? character.senses : []
          return onlyNonEmpty({ immunities, resistances, conditions, senses })
        }

        const traitsSection = () => {
          const features = Array.isArray(character.features) ? character.features : []
          const feats = Array.isArray(character.feats) ? character.feats : []
          const traits = Array.isArray(character.traits) ? character.traits : []
          const proficiencies = Array.isArray(character.proficiencies)
            ? character.proficiencies
            : []
          const languages = Array.isArray(character.languages) ? character.languages : []
          const advantages = Array.isArray(character.advantages) ? character.advantages : []
          return onlyNonEmpty({ features, feats, traits, proficiencies, languages, advantages })
        }

        const backgroundSection = () => {
          const backstory = character.backstory || null
          const background = character.background || null
          const race = character.race || null
          const subrace = character.subrace || null
          const alignment = character.alignment || null
          const appearance = character.appearance || character.distinguishingMarks || null
          const faith = character.faith || null
          const age = character.age || null
          const size = character.size || null
          return onlyNonEmpty({
            backstory,
            background,
            race,
            subrace,
            alignment,
            appearance,
            faith,
            age,
            size
          })
        }

        const personalitySection = () => {
          const p = character.personalityTraits || {}
          const inspiration = character.inspiration ?? null
          const notes = Array.isArray(character.notes) ? character.notes : []
          const distinguishingMarks = character.distinguishingMarks || null
          return onlyNonEmpty({ personalityTraits: p, inspiration, distinguishingMarks, notes })
        }

        const spellsSection = () => {
          const spells = character.spells || null
          if (!spells || typeof spells !== 'object') return null
          const {
            spell_save_dc,
            spell_attack_bonus,
            auto_spell_dc,
            auto_spell_attack,
            spell_slots,
            pact_magic,
            short_rest_recovery
          } = spells
          const counts = {}
          for (const key of Object.keys(spells)) {
            if (/^level\d+$/.test(key) || key === 'cantrips') {
              const arr = Array.isArray(spells[key]) ? spells[key] : []
              counts[key] = arr.length
            }
          }
          return onlyNonEmpty({
            spellSaveDC: spell_save_dc ?? spells.abilitySaveDC ?? null,
            spellAttackBonus: spell_attack_bonus ?? null,
            autoSpellDC: auto_spell_dc ?? null,
            autoSpellAttack: auto_spell_attack ?? null,
            slots: spell_slots || null,
            pactMagic: pact_magic || null,
            shortRestRecovery: short_rest_recovery || null,
            counts
          })
        }

        const notesSection = () => {
          const notes = Array.isArray(character.notes) ? character.notes : []
          return onlyNonEmpty({ notes })
        }

        const builders = {
          background: backgroundSection,
          personality: personalitySection,
          equipped: equipSection,
          inventory: inventorySection,
          actions: actionsSection,
          defenses: defensesSection,
          traits: traitsSection,
          spells: spellsSection,
          notes: notesSection
        }

        const result = {}
        for (const section of requested) {
          const build = builders[section]
          if (typeof build === 'function') {
            const value = build()
            if (value) result[section] = value
          }
        }

        return { ok: true, characterId: character.id || null, sections: result }
      } catch (e) {
        return { ok: false, error: e?.message || 'Failed to get character sections' }
      }
    }
  })
}

export { resolveCharacter }

